import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { bankAccountLines, checkPayAmount, formatDate, formatMoney, parseMoney } from "@rentorng/core";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { IconBank, IconCopy, IconSend } from "@/components/icons";
import { Alert, Badge, Button, Field, Input, Spinner, Textarea } from "@/components/ui";
import { MoneyInput } from "@/components/money-input";
import { Copyright } from "@/components/copyright";
import { RecaptchaNotice } from "@/components/recaptcha";
import { usePageTitle } from "@/lib/title";

/**
 * The pay link a tenant is sent.
 *
 * This is the only RentorNG screen a tenant ever sees, so it wears the **agency's** name, not
 * ours — the tenant has a relationship with their letting agent, and a page branded by software
 * they have never heard of reads like a phishing attempt.
 *
 * No session, no navigation, no way in to anything else. The API returns no ids and no other
 * invoices, because this URL gets forwarded — to a spouse, an employer, a WhatsApp group.
 */

interface PayAccount {
  id: string;
  label: string | null;
  currency: string;
  fields: Record<string, string>;
  instructions: string | null;
}

interface PayGateway {
  id: string;
  provider: string;
  label: string;
  blurb: string;
  publicKey: string | null;
  /**
   * Whose account it settles into.
   *
   * `platform` is ours: the checkout is started on the server, which is where the keys live.
   * `own` is the agency's, handed to the provider's widget with their public key — not yet wired,
   * and the page says so rather than offering a button that does nothing.
   */
  mode: "own" | "platform";
}

/**
 * One line of the invoice being paid.
 *
 * `description` is what the invoice said on the day it was raised — the agency's own words, put
 * there by `chargeLabel()` when it was built — and `formatted` is the amount written out the way
 * every other figure on this page is. Both arrive already decided: nothing on this page works out
 * what a charge is called or what it comes to, because a `label ?? typeName` written here would be
 * a second copy of the one rule that names a charge, and the day somebody changed a charge's type
 * it would rename a caution deposit on an invoice a tenant is holding. ADR-0110, ADR-0120.
 */
interface PayLine {
  type: string;
  description: string;
  amount: number;
  formatted: string;
}

interface PayResponse {
  number: string;
  dueDate: string;
  status: string;
  total: number;
  paid: number;
  balance: number;
  formatted: { total: string; balance: string; paid: string };
  lines?: PayLine[];
  currency: string;
  payer: string;
  unit: string | null;
  property: string | null;
  policy: { kind: "full" | "part"; min: number; max: number; suggested: number; editable: boolean; note: string };
  agency: { name: string; phone: string | null; email: string | null; address: string | null; logoUrl: string | null };
  payInto: PayAccount[];
  gateways: PayGateway[];
  pendingClaims: Array<{ id: string; amount: number; formatted: string; at: string }>;
}

type Choice = { kind: "transfer"; accountId: string } | { kind: "gateway"; gatewayId: string };

export function PayLinkPage() {
  const { token } = useParams();
  const queryClient = useQueryClient();

  /** Who is paying. Asked for here because a public payer has no account to read it from. */
  const [payerName, setPayerName] = useState("");
  const [payerEmail, setPayerEmail] = useState("");
  const [started, setStarted] = useState<{ simulated: boolean; redirectUrl: string | null } | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["pay", token],
    queryFn: () => api.get<PayResponse>(`/public/pay/${token}`),
    enabled: Boolean(token),
    retry: false,
  });

  const [choice, setChoice] = useState<Choice | null>(null);

  // Named for the invoice, because this tab is usually sitting beside a banking app while somebody
  // types a reference across. "RentorNG" tells them nothing at that moment; "INV-00096" does.
  usePageTitle(data?.number ? `Pay ${data.number}` : "Pay an invoice");

  /**
   * Start a card payment.
   *
   * The amount is not sent: the server reads it from the invoice. A payer who could name their
   * own amount could settle a million-naira invoice for one naira.
   */
  const checkout = useApiMutation({
    mutationFn: (input: { provider: string }) =>
      api.post<{ reference: string; redirectUrl: string | null; simulated: boolean }>(
        `/public/pay/${token}/checkout`,
        { provider: input.provider, email: payerEmail.trim(), name: payerName.trim() || undefined },
      ),
    onSuccess: (result) => {
      setStarted(result);
      // A simulated charge has nowhere real to go, so staying put and saying so beats a redirect
      // that lands back here with nothing to show for it.
      if (!result.simulated && result.redirectUrl) window.location.href = result.redirectUrl;
    },
  });
  const [amount, setAmount] = useState("");
  const [declaring, setDeclaring] = useState(false);

  // Default to the first method offered, and to the amount the invoice expects.
  useEffect(() => {
    if (!data || choice) return;
    if (data.gateways.length) setChoice({ kind: "gateway", gatewayId: data.gateways[0].id });
    else if (data.payInto.length) setChoice({ kind: "transfer", accountId: data.payInto[0].id });
  }, [data, choice]);

  useEffect(() => {
    if (data && amount === "") setAmount(String(data.policy.suggested / 100));
  }, [data, amount]);

  const money = useMemo(
    () => (value: number) => formatMoney(value, data?.currency ?? "NGN"),
    [data?.currency],
  );

  const declare = useMutation({
    mutationFn: (input: Record<string, unknown>) => api.postAsHuman<{ message: string }>(`/public/pay/${token}/claim`, input, "payment_claim"),
    onSuccess: async () => {
      setDeclaring(false);
      await queryClient.invalidateQueries({ queryKey: ["pay", token] });
    },
  });

  if (isLoading) return <PayShell><Spinner /></PayShell>;

  if (error || !data) {
    return (
      <PayShell>
        <div className="card p-6 text-center">
          <p className="text-base font-semibold text-danger-700">
            {error instanceof Error ? error.message : "This payment link could not be opened."}
          </p>
          <p className="mt-2 text-sm text-ink-500">
            It may have expired, or the invoice may have been cancelled. Contact the agency that sent it.
          </p>
          <Button variant="secondary" className="mt-4" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      </PayShell>
    );
  }

  const amountMinor = parseMoney(amount, data.currency) ?? 0;
  const amountProblem = data.policy.editable
    ? checkPayAmount(amountMinor, { ...data.policy, editable: true, note: data.policy.note }, money)
    : null;
  const selectedAccount = choice?.kind === "transfer" ? data.payInto.find((a) => a.id === choice.accountId) : undefined;
  const settled = data.balance <= 0;

  return (
    <PayShell agency={data.agency} wide>
      {/*
        One column on a phone. Two from `lg` up.

        The complaint was a real one: on a desktop this page was a 576px ribbon down the middle of a
        1920px screen, and the account number — the single thing anybody opens this link for — was
        below the fold behind an invoice they had already read. Widening the one column would have
        been worse, not better: a bank account rendered 900px wide is a line of digits with half a
        metre of nothing beside it, and the eye loses its place between the label and the value.

        So the card becomes a two-column grid, and **the split is a straight cut through the
        existing order rather than a re-arrangement**. Everything above the cut — who it is for, what
        is owed, what the money is made of, the notice about a payment already reported, and the
        amount box — is the invoice. Everything below it — how to pay, which account, the button
        that tells the agency — is the act of paying. Read the DOM top to bottom with the stylesheet
        off and you get exactly the page a phone shows today, in exactly that order; there is not one
        `order:` anywhere here, because there was never anything to reorder.

        **Below `lg` nothing at all changes.** No grid, no flex, the same blocks with the same
        padding and the same single hairline in the same place — the paddings either side of the cut
        are written as `pb-0 lg:pb-5` / `pt-6 lg:py-5` for precisely that reason, so the 24px gap a
        phone has always had between the amount box and "How would you like to pay?" survives the
        block boundary that now runs between them. This is a wide-screen enhancement; the phone is
        the primary case and it is untouched.

        **Deliberately not sticky.** Measured on a real invoice at 1280x720 — the laptop this has to
        work on — the right column is taller than the viewport on its own, so `position: sticky` with
        any top offset would pin a column that cannot fit and park its own button permanently off the
        bottom of the screen: the trap, not the fix. What actually solves the reported problem is the
        two columns themselves. A long left column no longer pushes the accounts down, so the pay
        method starts level with the invoice at the top of the screen rather than a screen and a half
        below it. `overflow-hidden` on the card would have disabled sticky anyway.
      */}
      <div className="card overflow-hidden lg:grid lg:grid-cols-2">
        {/* ------------------------------------------------------------------ the invoice.

            What is owed, who owes it, what it is made of, and — see the note on the block below —
            how much is being sent. Facts, and one input derived from them. */}
        <div data-pay-invoice className="min-w-0 lg:border-r lg:border-ink-100">
          <div
            className={`px-6 py-5 border-b border-ink-100${settled ? " lg:border-b-0" : ""}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="eyebrow">Invoice {data.number}</p>
                <h1 className="mt-1 text-2xl font-semibold tracking-tight">{data.formatted.balance}</h1>
                <p className="mt-1 text-sm text-ink-500">
                  {settled ? "Settled in full" : `Due ${formatDate(data.dueDate)}`}
                  {data.paid > 0 && !settled ? ` · ${data.formatted.paid} already paid of ${data.formatted.total}` : ""}
                </p>
              </div>
              {settled ? (
                <Badge tone="success">Paid</Badge>
              ) : data.policy.kind === "part" ? (
                <Badge tone="info">Part payment accepted</Badge>
              ) : (
                <Badge tone="muted">Full payment</Badge>
              )}
            </div>

            <p className="mt-4 text-sm text-ink-600">
              For <span className="font-medium text-ink-900">{data.payer}</span>
              {data.unit ? ` · ${data.unit}` : ""}
              {data.property ? `, ${data.property}` : ""}
            </p>

            <PayBreakdown lines={data.lines ?? []} total={data.formatted.total} />
          </div>

          {/*
            The notice about a payment already reported, and the amount.

            **"Amount to pay" sits at the foot of the invoice column, not at the head of the payment
            one** — the judgement call this layout turns on, and it went this way for three reasons.
            It is a figure about the invoice before it is an instruction to a bank: on a full-payment
            invoice it is not even a control, just the balance restated in a disabled box, and the
            sentence under it ("This invoice is settled in one payment", or the minimum a part payment
            may be) is a rule about *this invoice* that reads as nonsense a column away from the
            balance it constrains. It is also what the user asked for in their own words — the amount
            on the left. And it is what keeps the action reachable: those hundred-odd pixels are the
            difference between the "I have made this payment" button sitting at the fold on a 720px
            laptop and sitting below it.

            The cost is that the box and the button that consumes its value are in different columns,
            so when the amount is refused the button next to it is disabled for a reason written
            elsewhere. That is answered properly rather than left to be noticed: the message carries an
            id and the button points at it with `aria-describedby`, so a screen reader reads the
            refusal out with the button it disabled.
          */}
          {!settled ? (
            <div className="px-6 pb-0 pt-5 lg:pb-5">
              {data.pendingClaims.length > 0 ? (
                <div className="mb-5">
                  <Alert tone="info" title="You have already told them about a payment.">
                    {data.pendingClaims.map((claim) => claim.formatted).join(", ")} — waiting for the agency to confirm it
                    against their statement. The balance above updates when they do.
                  </Alert>
                </div>
              ) : null}

              {/* How much */}
              <div>
                <p className="label" id="pay-amount-label">
                  Amount to pay
                </p>
                {data.policy.editable ? (
                  <>
                    <MoneyInput
                      currency={String(data.currency ?? "NGN")}
                      value={amount}
                      onChange={setAmount}
                      className="text-lg font-semibold"
                    />
                    <p
                      id="pay-amount-note"
                      className={`mt-1.5 text-xs ${amountProblem ? "text-danger-600" : "text-ink-500"}`}
                    >
                      {amountProblem ?? data.policy.note}
                    </p>
                  </>
                ) : (
                  <>
                    <Input
                      value={data.formatted.balance}
                      disabled
                      className="text-lg font-semibold"
                      aria-labelledby="pay-amount-label"
                    />
                    <p id="pay-amount-note" className="mt-1.5 text-xs text-ink-500">
                      {data.policy.note}
                    </p>
                  </>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* ------------------------------------------------------------------ the payment.

            How, into which account, and the one thing the payer came here to press. A column of its
            own so that a long invoice beside it cannot push any of it down the screen. `lg:flex`
            with `lg:mt-auto` on the contact strip only so the strip settles on the bottom edge of
            the card when this column is the shorter of the two — on a phone the wrapper is a plain
            block and neither rule is in play. */}
        <div data-pay-action className="min-w-0 lg:flex lg:flex-col">
          {settled ? (
            <div className="px-6 py-8 text-center">
              <p className="text-base font-semibold text-brand-700">This invoice has been settled in full.</p>
              <p className="mt-1 text-sm text-ink-500">Thank you. Nothing further is owed on it.</p>
            </div>
          ) : (
            <div className="px-6 pb-5 pt-6 lg:py-5">
              {/* How */}
              <p className="label">How would you like to pay?</p>
              <div className="space-y-2">
                {data.gateways.map((gateway) => (
                  <MethodOption
                    key={gateway.id}
                    selected={choice?.kind === "gateway" && choice.gatewayId === gateway.id}
                    onSelect={() => setChoice({ kind: "gateway", gatewayId: gateway.id })}
                    icon={<IconSend />}
                    title={gateway.label}
                    subtitle={gateway.blurb}
                    badge="Instant"
                  />
                ))}

                {/* The details belong to the account that opened them, so they are rendered here
                    rather than under the whole list. Selection is a single value, so opening one
                    closes the other without any extra state to keep in step. */}
                {data.payInto.map((account) => {
                  const open = choice?.kind === "transfer" && choice.accountId === account.id;

                  return (
                    <div key={account.id}>
                      <MethodOption
                        selected={open}
                        expanded={open}
                        onSelect={() => setChoice({ kind: "transfer", accountId: account.id })}
                        icon={<IconBank />}
                        title={account.label ?? `${account.currency} bank transfer`}
                        subtitle={account.fields.bankName ? `Transfer to ${account.fields.bankName}` : "Bank transfer"}
                        /* The currency is the first thing that has to be right. Paying naira into a
                           dollar account is not a mistake anybody recovers from quickly, so it is
                           stated on the option itself rather than left to be inferred from the label. */
                        currency={account.currency}
                        foreign={account.currency.toUpperCase() !== data.currency.toUpperCase()}
                      />
                      {open ? <TransferDetails account={account} reference={data.number} flush /> : null}
                    </div>
                  );
                })}
              </div>

              {data.gateways.length === 0 && data.payInto.length === 0 ? (
                <div className="mt-4">
                  <Alert tone="warn">
                    This agency has not published a way to pay online yet. Contact them using the details below.
                  </Alert>
                </div>
              ) : null}

              {/* The chosen method */}
              {choice?.kind === "gateway" ? (
                (() => {
                  const gateway = data.gateways.find((entry) => entry.id === choice.gatewayId);
                  if (!gateway) return null;

                  return (
                    <div className="mt-5">
                      {checkout.errorMessage ? (
                        <div className="mb-3">
                          <Alert tone="danger">{checkout.errorMessage}</Alert>
                        </div>
                      ) : null}

                      {started?.simulated ? (
                        /*
                         * The one thing this page must never do is imply a payment happened.
                         * With no provider keys nothing was charged, and it says exactly that
                         * rather than showing a spinner that never resolves.
                         */
                        <Alert tone="warn" title="Nothing was charged.">
                          This agency's card payments are not connected to a provider yet, so no money was taken. Your
                          attempt was recorded — pay by transfer below, or contact them using the details at the bottom.
                        </Alert>
                      ) : gateway.mode === "own" ? (
                        <>
                          <Button className="w-full" disabled>
                            Continue to {gateway.label}
                          </Button>
                          <p className="mt-2 text-center text-xs text-ink-500">
                            This agency uses its own {gateway.provider} account, and that redirect is not wired up yet.
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="grid gap-3 sm:grid-cols-2">
                            <Input
                              value={payerName}
                              onChange={(event) => setPayerName(event.target.value)}
                              placeholder="Your name"
                              aria-label="Your name"
                            />
                            <Input
                              value={payerEmail}
                              onChange={(event) => setPayerEmail(event.target.value)}
                              type="email"
                              placeholder="Your email"
                              aria-label="Your email"
                            />
                          </div>
                          <Button
                            className="mt-3 w-full"
                            disabled={checkout.isPending || !payerEmail.trim()}
                            onClick={() => checkout.mutate({ provider: gateway.provider })}
                          >
                            {checkout.isPending ? "Starting…" : `Continue to ${gateway.label}`}
                          </Button>
                          <p className="mt-2 text-center text-xs text-ink-500">
                            We send the receipt to that address. You will be taken to {gateway.label.replace(/^Pay with /i, "")} to pay.
                          </p>
                        </>
                      )}
                    </div>
                  );
                })()
              ) : null}

              {/* The account details now sit inside the option that opened them, above. What is
                  left here is the action that follows having made the transfer. */}
              {selectedAccount ? (
                <div className="mt-5">
                  {declare.isSuccess ? (
                    <div className="mt-4">
                      <Alert tone="success" title="Thank you.">
                        {declare.data?.message}
                      </Alert>
                    </div>
                  ) : declaring ? (
                    <DeclareForm
                      amountMinor={amountMinor}
                      money={money}
                      accountId={selectedAccount.id}
                      payer={data.payer}
                      reference={data.number}
                      pending={declare.isPending}
                      error={declare.error instanceof Error ? declare.error.message : null}
                      onCancel={() => setDeclaring(false)}
                      onSubmit={(input) => declare.mutate(input)}
                    />
                  ) : (
                    <div className="mt-4">
                      {/* The primary action on this page: it is the only thing the payer is here
                          to do once the transfer is made. */}
                      <Button
                        className="w-full"
                        disabled={Boolean(amountProblem)}
                        /* The amount that would be declared is a column away on a wide screen, so
                           when it is refused the reason is announced with the button it disabled
                           rather than left sitting where somebody has to go and find it. */
                        aria-describedby={amountProblem ? "pay-amount-note" : undefined}
                        onClick={() => setDeclaring(true)}
                      >
                        I have made this payment
                      </Button>
                      <p className="mt-2 text-center text-xs text-ink-500">
                        Tell {data.agency.name} you have sent it, so they can match it and clear your balance.
                      </p>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}

          <div className="border-t border-ink-100 bg-ink-50 px-6 py-4 text-sm lg:mt-auto">
            <p className="font-semibold text-ink-800">Questions about this invoice?</p>
            {/* `break-words` because an agency's name, phone and email are run together on one line
                and none of the three is ours to break — a long address in a 480px column is exactly
                where a payment page starts scrolling sideways. */}
            <p className="mt-0.5 break-words text-ink-600">
              {data.agency.name}
              {data.agency.phone ? ` · ${data.agency.phone}` : ""}
              {data.agency.email ? ` · ${data.agency.email}` : ""}
            </p>
          </div>
        </div>
      </div>
    </PayShell>
  );
}

/**
 * What the money is for.
 *
 * This page asked somebody to send a sum and told them only its total, which is the one question a
 * payer opening it actually has — and it is the reason they ring the office before paying. The
 * lines existed on the invoice from the first migration; they were simply never sent here.
 *
 * **The words are the agency's own and are printed exactly as they arrive.** A charge somebody
 * named "Estate development levy" reads as that; one nobody named reads as "Other", because the
 * server's fallback for a line with no description of its own is `chargeLabel()`, and there is one
 * function that decides what a charge is called. This page must never be a second one.
 *
 * The total is repeated at the foot only where there is more than one line to add up. On a
 * single-line invoice the name is the whole of what was missing, and a "Total" row restating the
 * figure directly above it is a row that tells nobody anything.
 */
function PayBreakdown({ lines, total }: { lines: PayLine[]; total: string }) {
  if (!lines.length) return null;

  return (
    /* Marked so `verify-charge-label.mjs` can read the names out of this table rather than off the
       page — a search of the whole body would report a pass on any stray occurrence of the word
       "Other", which is exactly what a broken reader prints. */
    <div data-pay-lines className="mt-4 overflow-x-auto">
      <p className="label">What this is for</p>
      <table className="table mt-1.5">
        <tbody>
          {lines.map((line, index) => (
            <tr key={`${line.type}-${index}`}>
              <td className="font-medium">{line.description}</td>
              <td className="num tabular">{line.formatted}</td>
            </tr>
          ))}
          {lines.length > 1 ? (
            <tr>
              <td className="font-semibold">Total</td>
              <td className="num tabular font-semibold">{total}</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

/** The agency's page, with our name kept to a line at the bottom. */
function PayShell({
  children,
  agency,
  wide,
}: {
  children: React.ReactNode;
  agency?: PayResponse["agency"];
  /**
   * Let the column grow past `max-w-xl` from `lg` up, for the two-column invoice.
   *
   * Asked for rather than assumed, because this shell also carries the spinner and the "this link
   * could not be opened" card, and a two-line apology stretched across 1024px reads as a page that
   * has lost its contents. Those keep the narrow measure they have always had.
   *
   * `5xl` and not wider: past about a thousand pixels the two columns stop being two columns and
   * start being two unrelated pages, and a bank account number set in a 600px-wide field is harder
   * to copy by eye than one set in 450px, not easier.
   */
  wide?: boolean;
}) {
  /* One measure, applied to the bar, the body and the footer together — the agency's name sitting
     on a 576px header above a 1024px card is the sort of half-applied change that reads as a bug. */
  const measure = wide ? "max-w-xl lg:max-w-5xl" : "max-w-xl";

  /*
    A mark that fails to load falls back to the initials rather than leaving a broken square.
    This page is a payer's — somebody who has been sent a link and is deciding whether the thing in
    front of them is real. A broken image on it is not a cosmetic problem.

    Deliberately not `AgencyMark`, which the documents share: this header is dark, the fallback here
    is initials on a translucent tile rather than the agency's name in type, and forcing one
    component to do both would make the document treatment answerable to a page it has nothing else
    in common with.
  */
  const [markBroken, setMarkBroken] = useState(false);
  useEffect(() => setMarkBroken(false), [agency?.logoUrl]);

  return (
    <div className="flex min-h-screen flex-col bg-ink-50">
      <header className="bg-brand-900 text-white">
        <div className={`mx-auto flex ${measure} items-center gap-3 px-5 py-6`}>
          {agency?.logoUrl && !markBroken ? (
            <img
              src={agency.logoUrl}
              alt=""
              className="size-10 rounded-[var(--radius-control)] bg-white object-contain"
              onError={() => setMarkBroken(true)}
            />
          ) : (
            <span className="flex size-10 items-center justify-center rounded-[var(--radius-control)] bg-white/10 text-sm font-semibold">
              {(agency?.name ?? "R").slice(0, 2).toUpperCase()}
            </span>
          )}
          <div className="min-w-0">
            <p className="truncate text-lg font-semibold tracking-tight">{agency?.name ?? "Payment"}</p>
            {agency?.address ? <p className="truncate text-xs text-white/60">{agency.address}</p> : null}
          </div>
        </div>
      </header>

      <main className={`mx-auto w-full ${measure} flex-1 px-5 py-8`}>{children}</main>

      <footer className={`mx-auto w-full ${measure} px-5 pb-8 text-center`}>
        <p className="text-xs text-ink-400">
          Payments handled by {agency?.name ?? "the agency"}. Page provided by{" "}
          <Link to="/" className="font-medium hover:underline">
            RentorNG
          </Link>
          .
        </p>
        <Copyright className="mt-2" />
      </footer>
    </div>
  );
}

function MethodOption({
  selected,
  onSelect,
  icon,
  title,
  subtitle,
  badge,
  currency,
  foreign,
  expanded,
}: {
  selected: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  badge?: string;
  /** Shown as a hard-to-miss chip. Bank transfers only — a gateway takes whatever it is given. */
  currency?: string;
  /** True when this account is not in the currency of the invoice. */
  foreign?: boolean;
  /**
   * True when this option has its details open directly beneath it. The option stops being a
   * standalone box and becomes the header of one — square bottom corners, no bottom border — so
   * the pair reads as a single card rather than two stacked ones.
   */
  expanded?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-expanded={expanded === undefined ? undefined : expanded}
      className={`flex w-full items-center gap-3 border p-3.5 text-left transition-colors ${
        expanded ? "rounded-t-[var(--radius-surface)] border-b-0" : "rounded-[var(--radius-surface)]"
      } ${selected ? "border-brand-600 bg-brand-50" : "border-ink-200 bg-white hover:border-ink-300"}`}
    >
      <span
        className={`flex size-9 shrink-0 items-center justify-center rounded-full ${
          selected ? "bg-brand-600 text-white" : "bg-ink-100 text-ink-500"
        }`}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-ink-900">{title}</span>
        <span className="block truncate text-xs text-ink-500">{subtitle}</span>
      </span>
      {currency ? (
        <span
          className={`shrink-0 rounded-[var(--radius-control)] px-2.5 py-1 text-xs font-bold tracking-wide ${
            foreign ? "bg-warn-100 text-warn-700" : "bg-brand-100 text-brand-800"
          }`}
        >
          {currency.toUpperCase()}
        </span>
      ) : null}
      {badge ? <Badge tone="info">{badge}</Badge> : null}
      <span
        className={`size-4 shrink-0 rounded-full border-[5px] ${
          selected ? "border-brand-600 bg-white" : "border-ink-200 bg-white"
        }`}
      />
    </button>
  );
}

/** Every value on its own line with a copy button — a paragraph is what makes people mistype. */
function TransferDetails({
  account,
  reference,
  flush,
}: {
  account: PayAccount;
  reference: string;
  /** Rendered as the body of the option that opened it, rather than as a card of its own. */
  flush?: boolean;
}) {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      // Clipboard access can be refused; the value is on screen either way.
    }
  };

  return (
    <div
      /* Marked so `check-pay-page.mjs` can measure each Copy button against the panel that holds
         it rather than against the viewport. At the narrow end of the two-column range a button
         does not disappear, it gets pushed past the edge of this box — still in the DOM, still
         clickable from a script, and quite unpressable by a person. */
      data-pay-transfer
      className={
        flush
          ? // Sitting under the option that opened it: the option supplies the top border and
            // the rounded top corners, so this only closes the box off underneath.
            "rounded-b-[var(--radius-surface)] border border-t-0 border-brand-600 bg-white p-4"
          : "rounded-[var(--radius-surface)] border border-ink-200 bg-white p-4"
      }
    >
      {/* The currency is on the option this panel belongs to, a line above it. Saying it twice in
          the same eyeline reads as two different accounts rather than one. */}
      <p className="eyebrow mb-3">Transfer to</p>
      <dl className="space-y-2.5">
        {[...bankAccountLines(account), { key: "reference", label: "Reference", value: reference }].map((line) => (
          <div key={line.key} className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <dt className="text-xs text-ink-500">{line.label}</dt>
              <dd className="tabular truncate text-sm font-semibold text-ink-900">{line.value}</dd>
            </div>
            <button type="button" onClick={() => copy(line.key, line.value)} className="btn btn-secondary btn-sm shrink-0">
              <IconCopy size={14} />
              {copied === line.key ? "Copied" : "Copy"}
            </button>
          </div>
        ))}
      </dl>
      {account.instructions ? <p className="mt-3 text-xs italic text-ink-500">{account.instructions}</p> : null}
    </div>
  );
}

/**
 * "I have made the payment."
 *
 * Deliberately not a receipt: it tells the agency to go and look. The wording says so, because a
 * page that implies the balance is cleared when it is not causes exactly one phone call per
 * tenant.
 */
function DeclareForm({
  amountMinor,
  money,
  accountId,
  payer,
  reference,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  amountMinor: number;
  money: (value: number) => string;
  accountId: string;
  payer: string;
  reference: string;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (input: Record<string, unknown>) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);

  return (
    <form
      className="mt-4 rounded-[var(--radius-surface)] border border-brand-200 bg-brand-50/50 p-4"
      onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        onSubmit({
          amount: amountMinor,
          method: "bank_transfer",
          bankAccountId: accountId,
          reference: String(form.get("reference") || "") || null,
          paidOn: String(form.get("paidOn") || today),
          payerName: String(form.get("payerName") || payer),
          payerContact: String(form.get("payerContact") || "") || null,
          note: String(form.get("note") || "") || null,
        });
      }}
    >
      <p className="text-sm font-semibold text-ink-900">Tell them you have paid {money(amountMinor)}</p>
      <p className="mt-1 text-xs text-ink-600">
        This is a message, not a receipt — your balance changes when the agency matches it on their statement.
      </p>

      {error ? (
        <div className="mt-3">
          <Alert tone="danger">{error}</Alert>
        </div>
      ) : null}

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Field label="Your name" htmlFor="payerName" required>
          <Input id="payerName" name="payerName" required placeholder="Your name" defaultValue={payer} />
        </Field>
        <Field label="Phone or email" htmlFor="payerContact" hint="So they can reach you if it does not match.">
          <Input id="payerContact" name="payerContact" placeholder="Phone or email" />
        </Field>
        <Field label="Transfer reference" htmlFor="reference" hint="What your bank shows for it.">
          <Input id="reference" name="reference" placeholder="Teller or transfer reference" defaultValue={reference} />
        </Field>
        <Field label="Date paid" htmlFor="paidOn">
          <Input id="paidOn" name="paidOn" type="date" defaultValue={today} max={today} />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Anything else?" htmlFor="note">
            <Textarea id="note" name="note" rows={2} placeholder="Paid from my company account, so the name differs." />
          </Field>
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        <Button type="submit" disabled={pending}>
          {pending ? "Sending…" : "Send to the agency"}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      {/* This page is reached by a link in a message, by somebody who has never seen the product.
          It is the one form here that is not inside `AuthShell`, so it carries the line itself. */}
      <RecaptchaNotice />
    </form>
  );
}
